import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Progress,
  RadioGroup,
  RadioGroupItem,
  SYMPTOM_OPTIONS,
} from "@nfp-tracker/components";
import {
  analyzeObservationImport,
  batchObservationUpsertRows,
  checkNfpTrackerExportHeaders,
  formatBleedingForCsv,
  formatTemperature,
  parseCsv,
  planObservationImport,
  SUPPORT_MAILTO_URL,
  type ColumnMapping,
  type Database,
  type ImportConflictPolicy,
} from "@nfp-tracker/utils";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { AlertTriangle, CheckCircle2, FileUp, Loader2, MessageCircle } from "lucide-react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { observationsQueryKey } from "../hooks/useObservationsCache";
import { trackEvent } from "../integrations/analytics/client";
import { supabase } from "../integrations/supabase/client";
import { fetchObservationDates } from "../utils/observationsCsvIo";
import { SupportChatContext } from "./SupportChatContext";

/**
 * Import of NFP Tracker's own CSV export (restore a backup, or move data between accounts).
 * 0.5.0 deliberately accepts nothing else: files from other apps or spreadsheets are rejected
 * with a support prompt. The generic column-mapping engine in libs/utils stays unused here
 * until presets for other apps can be verified against real exports.
 */

type ObservationInsert = Database["public"]["Tables"]["observations"]["Insert"];
type Step = "file" | "review" | "importing" | "done";
type FileProblem =
  | { kind: "not-nfp-tracker" }
  | { kind: "legacy-export" }
  | { kind: "message"; text: string };

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_DATA_ROWS = 20_000;
const UPSERT_BATCH_SIZE = 500;
const MAX_ISSUES_SHOWN = 50;
const PREVIEW_ROWS = 8;
const KNOWN_SYMPTOMS = SYMPTOM_OPTIONS.map((s) => s.name);

interface ParsedFile {
  name: string;
  table: string[][];
  mapping: ColumnMapping;
  ignoredColumns: string[];
  unterminatedQuote: boolean;
}

interface ImportResult {
  written: number;
  newCount: number;
  overwriteCount: number;
  skippedExistingCount: number;
  errorRowCount: number;
  failed: string | null;
}

interface CsvImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ownerUserId: string;
  actorUserId: string | undefined;
}

export const CsvImportDialog = ({ open, onOpenChange, ownerUserId, actorUserId }: CsvImportDialogProps) => {
  const queryClient = useQueryClient();
  // Optional: the dialog also works outside the app shell, falling back to email.
  const supportChat = useContext(SupportChatContext);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>("file");
  const [fileProblem, setFileProblem] = useState<FileProblem | null>(null);
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [policy, setPolicy] = useState<ImportConflictPolicy>("skip");
  const [existingDates, setExistingDates] = useState<Set<string> | null>(null);
  const [existingError, setExistingError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<ImportResult | null>(null);

  const reset = () => {
    setStep("file");
    setFileProblem(null);
    setParsed(null);
    setPolicy("skip");
    setExistingDates(null);
    setExistingError(null);
    setProgress(0);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleOpenChange = (next: boolean) => {
    if (step === "importing") return; // never abandon a half-written import
    if (!next) reset();
    onOpenChange(next);
  };

  const contactSupport = () => {
    trackEvent("observations_csv_import_support_clicked", {});
    handleOpenChange(false);
    if (supportChat) supportChat.openSupportChat();
    else window.location.href = SUPPORT_MAILTO_URL;
  };

  const today = format(new Date(), "yyyy-MM-dd");

  const analysis = useMemo(
    () =>
      parsed
        ? analyzeObservationImport(parsed.table, {
            mapping: parsed.mapping,
            dateFormat: "iso",
            temperatureUnit: "F",
            knownSymptoms: KNOWN_SYMPTOMS,
            today,
            preserveMucusValues: true,
          })
        : null,
    [parsed, today]
  );

  const dateRangeFrom = analysis?.dateRange?.from;
  const dateRangeTo = analysis?.dateRange?.to;

  useEffect(() => {
    if (step !== "review") return;
    if (!dateRangeFrom || !dateRangeTo) {
      setExistingDates(new Set());
      return;
    }
    let cancelled = false;
    setExistingDates(null);
    setExistingError(null);
    fetchObservationDates(ownerUserId, dateRangeFrom, dateRangeTo)
      .then((dates) => {
        if (!cancelled) setExistingDates(dates);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setExistingError(error instanceof Error ? error.message : "Couldn't check existing entries.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [step, ownerUserId, dateRangeFrom, dateRangeTo]);

  const plan = useMemo(
    () =>
      analysis && existingDates
        ? planObservationImport(analysis, {
            ownerUserId,
            createdByUserId: actorUserId ?? null,
            existingDates,
            policy,
          })
        : null,
    [analysis, existingDates, ownerUserId, actorUserId, policy]
  );

  const existingInFile = useMemo(
    () =>
      analysis && existingDates
        ? analysis.validRows.filter((r) => existingDates.has(r.values!.observation_date)).length
        : 0,
    [analysis, existingDates]
  );

  const handleFile = async (file: File | undefined) => {
    setFileProblem(null);
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setFileProblem({ kind: "message", text: "That file is larger than 5 MB. Export a shorter date range and import each file." });
      return;
    }
    try {
      const text = await file.text();
      const { rows, unterminatedQuote } = parseCsv(text);
      if (rows.length === 0) {
        setFileProblem({ kind: "not-nfp-tracker" });
        return;
      }
      const check = checkNfpTrackerExportHeaders(rows[0]);
      if (check.status === "rejected") {
        trackEvent("observations_csv_import_rejected", { reason: check.reason });
        setFileProblem(
          check.reason === "legacy-export"
            ? { kind: "legacy-export" }
            : check.reason === "duplicate-columns"
              ? { kind: "message", text: "That file has the same column more than once. Re-export it from NFP Tracker and try again." }
              : { kind: "not-nfp-tracker" }
        );
        return;
      }
      if (rows.length < 2) {
        setFileProblem({ kind: "message", text: "That export has no observations in it." });
        return;
      }
      if (rows.length - 1 > MAX_DATA_ROWS) {
        setFileProblem({
          kind: "message",
          text: `That file has more than ${MAX_DATA_ROWS.toLocaleString()} rows. Export a shorter date range and import each file.`,
        });
        return;
      }
      setParsed({
        name: file.name,
        table: rows,
        mapping: check.mapping,
        ignoredColumns: check.ignoredColumns,
        unterminatedQuote,
      });
      setStep("review");
    } catch {
      setFileProblem({ kind: "message", text: "Couldn't read that file. Choose the .csv file NFP Tracker exported." });
    }
  };

  const runImport = async () => {
    if (!plan || !analysis || !parsed) return;
    setStep("importing");
    setProgress(0);
    // Batches are grouped by key set: overwrite rows can omit invalid cells and the creator.
    const batches = batchObservationUpsertRows(plan.rows, UPSERT_BATCH_SIZE);
    let written = 0;
    let failed: string | null = null;
    for (const batch of batches) {
      const { error } = await supabase
        .from("observations")
        .upsert(batch as ObservationInsert[], {
          onConflict: "owner_user_id,observation_date",
          // Belt and braces for "skip": a day added elsewhere since the preview stays untouched.
          ignoreDuplicates: policy === "skip",
        });
      if (error) {
        failed = error.message;
        break;
      }
      written += batch.length;
      setProgress(Math.round((written / plan.rows.length) * 100));
    }

    if (written > 0) {
      await queryClient.invalidateQueries({ queryKey: observationsQueryKey(ownerUserId) });
    }

    trackEvent("observations_csv_import_completed", {
      succeeded: failed === null,
      rowCount: parsed.table.length - 1,
      importedCount: written,
      newCount: plan.newCount,
      overwriteCount: plan.overwriteCount,
      skippedExistingCount: plan.skippedExistingCount,
      errorRowCount: analysis.errorCount,
      warningCount: analysis.warningCount,
      conflictPolicy: policy,
    });

    setResult({
      written,
      newCount: plan.newCount,
      overwriteCount: plan.overwriteCount,
      skippedExistingCount: plan.skippedExistingCount,
      errorRowCount: analysis.errorCount,
      failed,
    });
    setStep("done");
  };

  const issues = analysis?.rows.filter((r) => r.errors.length || r.warnings.length) ?? [];
  const previewRows = analysis?.validRows.slice(0, PREVIEW_ROWS) ?? [];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import an NFP Tracker export</DialogTitle>
          <DialogDescription>
            Restore a CSV exported from NFP Tracker, or move it to this account. You'll review everything
            before anything is saved.
          </DialogDescription>
        </DialogHeader>

        {step === "file" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Choose the .csv file from “Export Data → CSV”. Files from other apps or spreadsheets can't be
              imported yet.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => void handleFile(e.target.files?.[0])}
            />
            <Button variant="outline" className="w-full" onClick={() => fileInputRef.current?.click()}>
              <FileUp className="mr-2 h-4 w-4" />
              Choose CSV file
            </Button>
            {fileProblem?.kind === "not-nfp-tracker" && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="space-y-3">
                  <p>
                    This file wasn't exported from NFP Tracker. Importing from other apps isn't supported
                    yet — send us a sample export and we'll look at adding it.
                  </p>
                  <Button size="sm" variant="outline" onClick={contactSupport}>
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Contact support
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            {fileProblem?.kind === "legacy-export" && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  This export is from an older version of NFP Tracker that can't be imported reliably. Export
                  a new CSV from Settings → Export Data and import that file instead.
                </AlertDescription>
              </Alert>
            )}
            {fileProblem?.kind === "message" && (
              <Alert variant="destructive">
                <AlertDescription>{fileProblem.text}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {step === "review" && analysis && parsed && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{parsed.name}</span>
              {analysis.dateRange && ` · ${analysis.dateRange.from} to ${analysis.dateRange.to}`}
            </p>
            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
              <Stat label="Days in file" value={analysis.validRows.length} />
              <Stat label="Rows with errors" value={analysis.errorCount} tone={analysis.errorCount ? "bad" : undefined} />
              <Stat label="Warnings" value={analysis.warningCount} tone={analysis.warningCount ? "warn" : undefined} />
              <Stat
                label="Already logged"
                value={existingDates ? existingInFile : "…"}
                tone={existingInFile ? "warn" : undefined}
              />
            </div>

            {parsed.unterminatedQuote && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  The file ends inside a quoted value, so it may be cut off. Check the last rows below.
                </AlertDescription>
              </Alert>
            )}
            {parsed.ignoredColumns.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Extra column{parsed.ignoredColumns.length === 1 ? "" : "s"} ignored: {parsed.ignoredColumns.join(", ")}
              </p>
            )}
            {existingError && (
              <Alert variant="destructive">
                <AlertDescription>Couldn't check for existing entries: {existingError}</AlertDescription>
              </Alert>
            )}

            {existingInFile > 0 && (
              <div className="space-y-2 rounded-lg border p-3">
                <Label>{existingInFile.toLocaleString()} of these days already have entries</Label>
                <RadioGroup value={policy} onValueChange={(v) => setPolicy(v as ImportConflictPolicy)}>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="skip" id="csv-policy-skip" className="mt-0.5" />
                    <Label htmlFor="csv-policy-skip" className="font-normal">
                      Skip those days and keep what's already there
                    </Label>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="overwrite" id="csv-policy-overwrite" className="mt-0.5" />
                    <Label htmlFor="csv-policy-overwrite" className="font-normal">
                      Overwrite them with the file's values (blank cells clear that field; cells with a
                      warning keep the current value)
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {previewRows.length > 0 && (
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-left text-xs">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-2 py-1 font-medium">Date</th>
                      <th className="px-2 py-1 font-medium">Temp</th>
                      <th className="px-2 py-1 font-medium">Bleeding</th>
                      <th className="px-2 py-1 font-medium">Mucus</th>
                      <th className="px-2 py-1 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row) => {
                      const v = row.values!;
                      const exists = existingDates?.has(v.observation_date);
                      return (
                        <tr key={row.line} className="border-t">
                          <td className="whitespace-nowrap px-2 py-1">
                            {v.observation_date}
                            {v.is_cycle_start ? " · start" : ""}
                          </td>
                          <td className="px-2 py-1">{v.temperature_f != null ? `${formatTemperature(v.temperature_f)}°F` : ""}</td>
                          <td className="px-2 py-1">{formatBleedingForCsv(v.bleeding)}</td>
                          <td className="max-w-[10rem] truncate px-2 py-1">{v.mucus ?? ""}</td>
                          <td className="whitespace-nowrap px-2 py-1 text-muted-foreground">
                            {existingDates ? (exists ? (policy === "skip" ? "Skip" : "Overwrite") : "New") : "…"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {analysis.validRows.length > PREVIEW_ROWS && (
                  <p className="border-t px-2 py-1 text-xs text-muted-foreground">
                    …and {(analysis.validRows.length - PREVIEW_ROWS).toLocaleString()} more
                  </p>
                )}
              </div>
            )}

            {issues.length > 0 && (
              <div className="space-y-1">
                <Label>Issues</Label>
                <ul className="max-h-48 space-y-1 overflow-y-auto rounded-lg border p-2 text-xs">
                  {issues.slice(0, MAX_ISSUES_SHOWN).map((row) => (
                    <li key={row.line}>
                      <span className="font-medium">Line {row.line}:</span>{" "}
                      {row.errors.length > 0 && (
                        <span className="text-destructive">{row.errors.join("; ")} — not imported. </span>
                      )}
                      {row.warnings.length > 0 && (
                        <span className="text-amber-700 dark:text-amber-400">{row.warnings.join("; ")}</span>
                      )}
                    </li>
                  ))}
                  {issues.length > MAX_ISSUES_SHOWN && (
                    <li className="text-muted-foreground">…and {issues.length - MAX_ISSUES_SHOWN} more rows with issues</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}

        {step === "importing" && (
          <div className="space-y-3 py-4">
            <p className="flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Importing… keep this window open.
            </p>
            <Progress value={progress} />
          </div>
        )}

        {step === "done" && result && (
          <div className="space-y-3">
            {result.failed ? (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  The import stopped partway: {result.failed}. {result.written.toLocaleString()} day
                  {result.written === 1 ? " was" : "s were"} saved. Importing the same file again with “Skip”
                  picks up where it left off.
                </AlertDescription>
              </Alert>
            ) : (
              <p className="flex items-center gap-2 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Import complete
              </p>
            )}
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>{result.newCount.toLocaleString()} new days</li>
              {result.overwriteCount > 0 && <li>{result.overwriteCount.toLocaleString()} existing days updated</li>}
              {result.skippedExistingCount > 0 && (
                <li>{result.skippedExistingCount.toLocaleString()} days skipped because they already had entries</li>
              )}
              {result.errorRowCount > 0 && <li>{result.errorRowCount.toLocaleString()} rows not imported due to errors</li>}
            </ul>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {step === "review" && (
            <>
              <Button variant="outline" onClick={reset}>
                Choose another file
              </Button>
              <Button disabled={!plan || plan.rows.length === 0} onClick={() => void runImport()}>
                {!existingDates && !existingError ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {plan
                  ? plan.rows.length === 0
                    ? "Nothing to import"
                    : `Import ${plan.rows.length.toLocaleString()} day${plan.rows.length === 1 ? "" : "s"}`
                  : "Import"}
              </Button>
            </>
          )}
          {step === "done" && <Button onClick={() => handleOpenChange(false)}>Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ label, value, tone }: { label: string; value: number | string; tone?: "warn" | "bad" }) => (
  <div className="rounded-lg border p-2">
    <p
      className={
        tone === "bad"
          ? "text-lg font-semibold text-destructive"
          : tone === "warn"
            ? "text-lg font-semibold text-amber-600 dark:text-amber-400"
            : "text-lg font-semibold"
      }
    >
      {typeof value === "number" ? value.toLocaleString() : value}
    </p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);
